import React from 'react';
import { Divider, Grid, Skeleton } from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { RootState } from '../../../../state/store';
import { useAppDispatch, useAppSelector } from '../../../../state/hooks';
import { setNlpResultFalse, toggleEnableReviewTableDialog } from './reviewTableSlice';
import { setEnableAudioMappingTable } from '../../../../features/SpeechRecognition/audioSlice';
import { createScenario } from '../../../../features/scenarioCreation/scenarioCreationSlice';
import { openPopup } from '../../../../features/ui/popupSlice';
import Swal from 'sweetalert2';
import AlertPopup from '../../../../components/AlertPopup/AlertPopup';
import { ReviewResultTable } from '../../../../components/ReviewResultTable/ReviewResultTable';
import {
  BackBtnToInputScreen,
  ConfirmationSection,
  ReviewTableContainer,
  ReviewTableHeading,
  ReviewTableSkeletonGrid,
  RunBtnToOutputScreen,
} from './ReviewTable.Styled';
import { showLoader } from '../../../../features/ui/loaderSlice';
import { getOutputOverview, resetOutputOverviewState } from '../../../../features/outputOverview/outputOverviewSlice';
import { fetchOutputFilters } from '../../../../features/outputFilters/outputfilterSlice';
import { convertKeysToLowerCaseAndUnderscore } from '../../../../utils/helper';
import { setDistributionSelected, setPriceInput } from '../../../../components/FormComponent/formSlice';

interface ReviewTableProps {
  onClose: () => void;
}

export const ReviewTable: React.FC<ReviewTableProps> = ({ onClose }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const { loading, isNlpResult } = useAppSelector((state: RootState) => state.reviewTable);
  const filtersState = useAppSelector((state: RootState) => state.filters);
  const inputMappingTable = useAppSelector((state: RootState) => state.reviewTable.mappings);
  const { scenarioName, scenario_action } = useAppSelector((state: RootState) => state.scenarioCreation);

  const inputMappingTableError = useAppSelector((state: RootState) => state.reviewTable.error);

  const handleBackButton = () => {
    dispatch(toggleEnableReviewTableDialog(false));
    dispatch(setEnableAudioMappingTable(false));
    dispatch(setPriceInput(0));
    dispatch(setDistributionSelected(''));
    onClose();
    if (isNlpResult === true) {
      dispatch(setNlpResultFalse());
    }
  };

  const handleRunScenario = async () => {
    if (!scenarioName) {
      dispatch(openPopup('Scenario name cannot be empty!'));
      return;
    }
    const payload = {
      mapping: convertKeysToLowerCaseAndUnderscore(inputMappingTable),
      scenario_name: scenarioName,
      scenario_action: scenario_action,
    };

    try {
      const response = await dispatch(createScenario(payload)).unwrap();
      Swal.fire({
        text: response.message,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#DF2020',
      }).then(async (result) => {
        if (result.isConfirmed) {
          navigate('/scenario-result');
          await dispatch(resetOutputOverviewState());
          await dispatch(showLoader());

          await dispatch(getOutputOverview({}));
          await dispatch(
            fetchOutputFilters({
              scenario_names: [`${scenarioName}`],
              state: filtersState.enabledKeys.state || [],
              bottler: filtersState.enabledKeys.bottler || [],
            }),
          );
        }
      });
    } catch (error: any) {
      const errorMessage =
        typeof error === 'string'
          ? error
          : typeof error === 'string' && error.length > 100
          ? `${error.substring(0, 100)}...`
          : error.message || 'An unknown error occurred heyeyeyyeye';

      Swal.fire({
        confirmButtonColor: '#DF2020',
        text: errorMessage,
        icon: 'error',
        iconColor: '#DF2020',
        confirmButtonText: 'OK',
      });
    }
  };

  return (
    <ReviewTableContainer>
      <Grid style={{ padding: '10px', paddingBottom: '0px' }}>
        <ReviewTableHeading>Review Table</ReviewTableHeading>
        <Divider style={{ marginTop: '10px', color: '#e6ecf0' }} />
      </Grid>
      <ReviewTableSkeletonGrid>
        {loading ? (
          <Grid
            style={{
              width: '100%',
              height: 535,
              marginTop: '15px',
            }}
          >
            <Skeleton variant="rectangular" height={250} />
          </Grid>
        ) : (
          <ReviewResultTable mappings={inputMappingTable} error={inputMappingTableError} />
        )}
      </ReviewTableSkeletonGrid>
      <ConfirmationSection>
        <BackBtnToInputScreen onClick={handleBackButton}>Back</BackBtnToInputScreen>
        <RunBtnToOutputScreen onClick={handleRunScenario}>Run Scenario</RunBtnToOutputScreen>
      </ConfirmationSection>
      <AlertPopup message={useAppSelector((state) => state.popup.message)} onConfirm={() => dispatch(openPopup(''))} />
    </ReviewTableContainer>
  );
};
